import { clone, EmployeeTagDTO, randomColor } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ColorInput } from "../../components/color-picker/color-input";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import "../../elements/sortable-list";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";

@customElement("ptc-settings-core-data")
export class SettingsCoreData extends Routing(StateMixin(LitElement)) {
    routePattern = /settings\/core-data/;

    get routeTitle() {
        return "Dokumente | Stammdaten";
    }

    get helpPage() {
        return "handbuch/einstellungen/stammdaten/";
    }

    @state()
    private _loading = false;

    @state()
    private _existingEmployeeTags: EmployeeTagDTO[] = [];

    @state()
    private _employeeTags: EmployeeTagDTO[] = [];

    private get _hasChanges() {
        return (
            this._employeeTags.length !== this._existingEmployeeTags.length ||
            this._employeeTags.some((tag) => {
                const existing = this._existingEmployeeTags.find((t) => t.id === tag.id);
                return !existing || existing.name !== tag.name || existing.color !== tag.color;
            })
        );
    }

    handleRoute() {
        const fetchTags = async () => {
            try {
                const tags = await app.api.getEmployeeTagsForCompany();

                // to detect changes correctly, we need to make sure that available tags and employee tags don't share references
                this._employeeTags = clone(tags);
                this._existingEmployeeTags = tags;
            } catch (e) {
                void alert(e.message, { type: "warning" });
            }
        };

        void fetchTags();
    }

    private _reset() {
        // to detect changes correctly, we need to make sure that available tags and employee tags don't share references
        this._employeeTags = clone(this._existingEmployeeTags);
    }

    private async _submit(e: Event) {
        e.preventDefault();

        this._loading = true;

        try {
            await app.updateCompany({ employeeTags: this._employeeTags });

            // to detect changes correctly, we need to make sure that available tags and employee tags don't share references
            this._existingEmployeeTags = clone(this._employeeTags);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _removeEmployeeTag(index: number) {
        this._employeeTags.splice(index, 1);
        this.requestUpdate();
    }

    private async _newEmployeeTag() {
        this._employeeTags.push(
            new EmployeeTagDTO({
                name: "",
                color: randomColor(this._existingEmployeeTags.map((t) => t.color)),
                order: this._employeeTags.length,
            })
        );
        this.requestUpdate();
        await this.updateComplete;
        const tagWrappers = this.renderRoot.querySelectorAll(".employees .tag-item");
        const nameInput = tagWrappers[tagWrappers.length - 1]?.querySelector("input");
        nameInput?.focus();
    }

    static styles = [
        shared,
        ColorInput.styles,
        css`
            .tag-item > input {
                padding: 0.7em 2em 0.7em 3em;
                width: 100%;
            }

            .tag-item > input:focus-visible {
                box-shadow: inset var(--color-primary) 0 0 0 1px;
            }

            .tag-item > ptc-color-input {
                position: absolute;
                left: 0.5em;
                top: 0.5em;
            }

            .tag-item > button {
                position: absolute;
                right: 0.5em;
                top: 0.4em;
            }

            .tag-item:not(:hover) > button {
                opacity: 0;
            }
        `,
    ];

    render() {
        if (!app.company) {
            return;
        }
        return html`
            <ptc-scroller class="fullbleed">
                <div class="double-padded narrow-content">
                    <form @submit=${this._submit} @input=${() => this.requestUpdate()} autocomplete="off">
                        <h2>Fähigkeiten & Merkmale</h2>
                        <div class="box employee">
                            <ptc-sortable-list
                                .items=${this._employeeTags}
                                .renderItem=${(tag: EmployeeTagDTO, i: number) => html`
                                    <div class="tag-item border-bottom relative">
                                        <ptc-color-input
                                            name="color"
                                            .value=${tag.color}
                                            required
                                            @change=${(e: Event) => (tag.color = (e.target as HTMLInputElement).value)}
                                            class="right-margined"
                                        ></ptc-color-input>
                                        <input
                                            name="name"
                                            .value=${tag.name}
                                            required
                                            @input=${(e: Event) =>
                                                (tag.name = (e.target as HTMLInputElement).value.trim())}
                                            placeholder="Fähigkeit oder Merkmal eingeben..."
                                            class="plain"
                                        />
                                        <button
                                            class="skinny transparent remove-button"
                                            @click=${() => this._removeEmployeeTag(i)}
                                            type="button"
                                        >
                                            <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                        </button>
                                    </div>
                                `}
                                @item-moved=${this._submit}
                            >
                            </ptc-sortable-list>

                            <div>
                                <button
                                    class="transparent fill-horizontally"
                                    type="button"
                                    @click=${this._newEmployeeTag}
                                >
                                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neue Fähigkeit oder Merkmal
                                </button>
                            </div>
                        </div>

                        <div
                            class="padded top-margined horizontal spacing evenly stretching layout"
                            ?hidden=${!this._hasChanges}
                        >
                            <button class="primary">Speichern</button>
                            <button type="button" class="transparent" @click=${this._reset}>Abbrechen</button>
                        </div>
                    </form>
                </div>
            </ptc-scroller>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
